import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon, Logo, type IconName } from '../components/Icon';
import { initials } from '../lib/format';
import { can, ROLE_LABELS } from '../lib/domain';
import { PasswordModal } from '../features/auth/PasswordModal';
import { useAuth, useCurrentUser } from './AuthProvider';
import { useTenant } from './TenantScope';

function NavItem({ to, icon, label, count }: { to: string; icon: IconName; label: string; count?: number }) {
  return (
    <NavLink to={to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} title={label}>
      <Icon name={icon} />
      <span className="label">{label}</span>
      {count ? <span className="count num">{count}</span> : null}
    </NavLink>
  );
}

function TenantNav() {
  const { tenant, api, role, base } = useTenant();
  const alerts = useQuery({ queryKey: ['alerts', tenant.id], queryFn: api.alerts.list, refetchInterval: 30_000 });
  const critical = alerts.data?.filter((a) => a.severity === 'critical').length ?? 0;
  return (
    <>
      {role === 'platform_admin' && (
        <div className="tenant-card">
          <small>Espace client</small>
          <b>{tenant.name}</b>
        </div>
      )}
      <div className="nav-section">{role === 'platform_admin' ? 'Ce client' : tenant.name}</div>
      <NavItem to={`${base}/dashboard`} icon="dashboard" label="Tableau de bord" />
      <NavItem to={`${base}/plans`} icon="plan" label="Plans et vues 3D" />
      <NavItem to={`${base}/assets`} icon="cart" label="Matériels et tags" />
      <NavItem to={`${base}/picking`} icon="pick" label="Prélèvements" />
      <NavItem to={`${base}/scan`} icon="scan" label="Scanner (mobile)" />
      <NavItem to={`${base}/alerts`} icon="alert" label="Alertes" count={critical} />
      <NavItem to={`${base}/analytics`} icon="chart" label="Analyses" />
      {can.manageUsers(role) && <NavItem to={`${base}/users`} icon="users" label="Utilisateurs" />}
      {can.manageUsers(role) && <NavItem to={`${base}/integration`} icon="plug" label="Intégration" />}
    </>
  );
}

function AccountMenu() {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  return (
    <div className="account">
      <button type="button" className="account-button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="avatar">{initials(user.name || user.email)}</span>
        <div>
          <b>{user.name}</b>
          <small>{ROLE_LABELS[user.role]}</small>
        </div>
      </button>
      {open && (
        <div className="card menu account-menu" role="menu">
          <div className="muted small" style={{ padding: '4px 10px 8px' }}>{user.email}</div>
          <button type="button" role="menuitem" className="menu-item" onClick={() => { setChangingPassword(true); setOpen(false); }}><Icon name="key" size={16} />Changer le mot de passe</button>
          <a role="menuitem" className="menu-item" href="/api/docs" target="_blank" rel="noreferrer"><Icon name="docs" size={16} />Documentation de l’API</a>
          <button type="button" role="menuitem" className="menu-item" onClick={async () => { await logout(); navigate('/login'); }}><Icon name="logout" size={16} />Se déconnecter</button>
        </div>
      )}
      {changingPassword && <PasswordModal onClose={() => setChangingPassword(false)} />}
    </div>
  );
}

/**
 * Main layout: sidebar navigation and the page content. On phones the sidebar
 * becomes a drawer opened from the top bar.
 */
export function Shell({ children, inTenant }: { children: ReactNode; inTenant: boolean }) {
  const user = useCurrentUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setMenuOpen(false), [pathname]);
  return (
    <div className={`shell${menuOpen ? ' menu-open' : ''}`}>
      <header className="mobile-bar">
        <button type="button" className="btn ghost icon" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}><Icon name={menuOpen ? 'close' : 'menu'} size={20} /></button>
        <Logo size={26} /><b>Gonio</b>
      </header>
      {menuOpen && <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <nav className="sidebar" aria-label="Navigation principale">
        <div className="brand"><Logo /><div><b>Gonio</b><small>Suivi BLE AoA et RFID</small></div></div>
        {user.role === 'platform_admin' && <>
          <NavItem to="/admin/overview" icon="globe" label="Vue d’ensemble" />
          <NavItem to="/admin/tenants" icon="building" label="Clients" />
        </>}
        {inTenant && <TenantNav />}
        <div className="spacer" />
        <AccountMenu />
      </nav>
      <main className="main">{children}</main>
    </div>
  );
}
